/**
 * DOM-free reactive primitives (architecture §3.10): `signal`, `computed`, `effect`, `batch`, `Scope`. No UI framework;
 * the DOM helper `h()` lives in `src/ui/dom.ts` and binds these.
 *
 * Semantics:
 * - `signal.set(v)` with an `Object.is`-equal value is a no-op.
 * - `computed(fn)` is lazy and auto-tracks every `get()` made while it runs; it recomputes on the next read after a
 *   dependency changed.
 * - `effect(fn)` runs immediately, re-runs after any tracked dependency changes (after the outermost `set`/`batch`
 *   finishes), and runs the cleanup `fn` returned before each re-run and on dispose.
 * - `subscribe(fn)` calls `fn(value)` on every CHANGE after subscribing (not immediately; read `get()` for the current
 *   value). Change = not `Object.is`-equal to the last value delivered.
 * - `batch(fn)` defers effects and subscriptions until `fn` returns: one notification pass.
 */

/** Read-only view of a reactive value. */
export interface ReadonlySignal<T> { get(): T; subscribe(fn: (v: T) => void): () => void }
/** A writable reactive value. */
export interface Signal<T> extends ReadonlySignal<T> { set(v: T): void; update(fn: (v: T) => T): void }

interface Source { readonly observers: Set<Observer> }
interface Observer { readonly sources: Set<Source>; markDirty(): void }

let current: Observer | null = null;
let batchDepth = 0;
const pending = new Set<{ run(): void }>();

function track(src: Source): void {
  if (!current) return;
  current.sources.add(src);
  src.observers.add(current);
}

function untrackAll(obs: Observer): void {
  for (const s of obs.sources) s.observers.delete(obs);
  obs.sources.clear();
}

function notify(src: Source): void {
  for (const o of [...src.observers]) o.markDirty();
}

function flush(): void {
  if (batchDepth > 0) return;
  let guard = 0;
  while (pending.size > 0) {
    if (++guard > 10_000) throw new Error('reactive: effect cycle detected (an effect keeps re-triggering itself)');
    const list = [...pending];
    pending.clear();
    for (const e of list) e.run();
  }
}

function withObserver<R>(obs: Observer | null, fn: () => R): R {
  const prev = current;
  current = obs;
  try { return fn(); } finally { current = prev; }
}

/** Runs `fn` without tracking any `get()` it makes. */
export function untrack<R>(fn: () => R): R { return withObserver(null, fn); }

class SubscriptionNode<T> implements Observer {
  readonly sources = new Set<Source>();
  private last: T;
  private alive = true;
  constructor(private readonly read: () => T, private readonly src: Source, private readonly fn: (v: T) => void) {
    this.last = untrack(read);
    this.sources.add(src);
    src.observers.add(this);
  }
  markDirty(): void { if (this.alive) pending.add(this); }
  run(): void {
    if (!this.alive) return;
    const v = untrack(this.read);
    if (Object.is(v, this.last)) return;
    this.last = v;
    this.fn(v);
  }
  dispose(): void { this.alive = false; untrackAll(this); pending.delete(this); }
}

class SignalImpl<T> implements Signal<T>, Source {
  readonly observers = new Set<Observer>();
  constructor(private value: T) {}
  get(): T { track(this); return this.value; }
  set(v: T): void {
    if (Object.is(v, this.value)) return;
    this.value = v;
    notify(this);
    flush();
  }
  update(fn: (v: T) => T): void { this.set(fn(this.value)); }
  subscribe(fn: (v: T) => void): () => void {
    const node = new SubscriptionNode(() => this.value, this, fn);
    return () => node.dispose();
  }
}

class ComputedImpl<T> implements ReadonlySignal<T>, Source, Observer {
  readonly observers = new Set<Observer>();
  readonly sources = new Set<Source>();
  private dirty = true;
  private value!: T;
  constructor(private readonly fn: () => T) {}
  markDirty(): void {
    if (this.dirty) return;
    this.dirty = true;
    notify(this);
  }
  get(): T {
    track(this);
    if (this.dirty) {
      untrackAll(this);
      this.value = withObserver(this, this.fn);
      this.dirty = false;
    }
    return this.value;
  }
  subscribe(fn: (v: T) => void): () => void {
    const node = new SubscriptionNode(() => this.get(), this, fn);
    return () => node.dispose();
  }
}

class EffectNode implements Observer {
  readonly sources = new Set<Source>();
  private cleanup: (() => void) | void = undefined;
  private alive = true;
  constructor(private readonly fn: () => void | (() => void)) {}
  markDirty(): void { if (this.alive) pending.add(this); }
  run(): void {
    if (!this.alive) return;
    if (typeof this.cleanup === 'function') this.cleanup();
    untrackAll(this);
    this.cleanup = withObserver(this, this.fn);
  }
  dispose(): void {
    if (!this.alive) return;
    this.alive = false;
    pending.delete(this);
    untrackAll(this);
    if (typeof this.cleanup === 'function') this.cleanup();
    this.cleanup = undefined;
  }
}

/** A writable reactive value. */
export function signal<T>(v: T): Signal<T> { return new SignalImpl(v); }

/** A lazily derived value; auto-tracks `get()` calls made by `fn`. */
export function computed<T>(fn: () => T): ReadonlySignal<T> { return new ComputedImpl(fn); }

/** Runs `fn` now and again whenever a tracked dependency changes; returns the dispose function. */
export function effect(fn: () => void | (() => void)): () => void {
  const node = new EffectNode(fn);
  node.run();
  return () => node.dispose();
}

/** Defers every effect/subscription until `fn` returns: one notification pass. Nested batches are flattened. */
export function batch(fn: () => void): void {
  batchDepth++;
  try { fn(); } finally { batchDepth--; flush(); }
}

/** True when `x` looks like a signal (has `get` and `subscribe`). */
export function isSignal<T = unknown>(x: unknown): x is ReadonlySignal<T> {
  return typeof x === 'object' && x !== null && typeof (x as ReadonlySignal<T>).get === 'function' &&
    typeof (x as ReadonlySignal<T>).subscribe === 'function';
}

/** Owns disposers; `dispose()` runs them in reverse order exactly once. Screens get one auto-disposed scope. */
export class Scope {
  private disposers: Array<() => void> = [];
  private disposed = false;
  /** Registers a disposer (runs immediately if the scope is already disposed). */
  add(dispose: () => void): void {
    if (this.disposed) { dispose(); return; }
    this.disposers.push(dispose);
  }
  /** `effect(fn)` owned by this scope. */
  effect(fn: () => void | (() => void)): void { this.add(effect(fn)); }
  /** True after `dispose()`. */
  get isDisposed(): boolean { return this.disposed; }
  /** Runs every disposer in reverse registration order; errors are reported and do not stop the others. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const list = this.disposers;
    this.disposers = [];
    for (let i = list.length - 1; i >= 0; i--) {
      try { list[i](); } catch (err) { console.error('[scope] disposer threw', err); }
    }
  }
}
